import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';

const priorityColorMap = {
	High: 'signal.red',
	Normal: 'signal.amber',
	Low: 'signal.green',
};

const SalesforceTaskRow = ({ task, onClick }) => {
	const priorityColor = priorityColorMap[task.priority] || 'signal.gray';

	return (
		<Box
			onClick={() => onClick(task)}
			sx={{
				display: 'flex',
				alignItems: 'flex-start',
				gap: '12px',
				padding: '10px 15px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
				cursor: 'pointer',
				transition: 'background-color 0.2s',
				'&:hover': {
					backgroundColor: 'background.subtle',
				},
			}}
		>
			{/* Priority Dot */}
			<Box
				sx={{
					width: 8,
					height: 8,
					minWidth: 8,
					borderRadius: '50%',
					backgroundColor: priorityColor,
					mt: '5px',
					flexShrink: 0,
				}}
			/>

			{/* Main Content */}
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'baseline',
						gap: '8px',
						mb: '2px',
					}}
				>
					<Typography
						sx={{
							fontSize: '12.5px',
							fontWeight: 600,
							color: 'text.primary',
							lineHeight: 1.4,
						}}
					>
						{task.subject || '(No Subject)'}
					</Typography>
					<Typography
						sx={{
							fontSize: '11px',
							color: 'text.tertiary',
							fontWeight: 500,
						}}
					>
						{task.due_date ? `Due ${task.due_date}` : 'No due date'}
					</Typography>
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
					<Typography
						sx={{
							fontSize: '11px',
							color: 'text.secondary',
							display: 'flex',
							alignItems: 'center',
						}}
					>
						<Box
							component='span'
							sx={{
								color: priorityColor,
								fontWeight: 700,
								mr: '4px',
								fontSize: '10px',
								textTransform: 'uppercase',
							}}
						>
							{task.priority}
						</Box>
						· {task.status}
					</Typography>
				</Box>
			</Box>

			{/* Right-justified context */}
			{(task.comments || task.related_to || task.name) && (
				<Box
					sx={{
						maxWidth: '40%',
						textAlign: 'right',
						display: 'flex',
						flexDirection: 'column',
						gap: '2px',
						flexShrink: 0,
					}}
				>
					{task.comments && (
						<Typography
							sx={{
								fontSize: '11px',
								color: 'text.secondary',
								fontStyle: 'italic',
								display: '-webkit-box',
								WebkitLineClamp: 1,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
								lineHeight: 1.3,
							}}
						>
							{`"${task.comments}"`}
						</Typography>
					)}
					{(task.related_to || task.name) && (
						<Typography
							sx={{
								fontSize: '10px',
								fontWeight: 600,
								color: 'primary.main',
								display: '-webkit-box',
								WebkitLineClamp: 1,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden',
							}}
						>
							{[task.related_to, task.name].filter(Boolean).join(' · ')}
						</Typography>
					)}
				</Box>
			)}
		</Box>
	);
};

SalesforceTaskRow.propTypes = {
	task: PropTypes.shape({
		id: PropTypes.string.isRequired,
		subject: PropTypes.string,
		status: PropTypes.string,
		priority: PropTypes.string,
		due_date: PropTypes.string,
		comments: PropTypes.string,
		related_to: PropTypes.string,
		name: PropTypes.string,
	}).isRequired,
	onClick: PropTypes.func.isRequired,
};

export default SalesforceTaskRow;
